import React from 'react';
import PropTypes from 'prop-types';
import StatCard from '../../../../components/StatCard/StatCard';
import {
	EM_DASH,
	formatNumber,
	formatRate,
	formatUtcHHmm,
	formatEtTime,
	minutesSince,
} from './queueMonitorFormat';

/**
 * ThroughputStrip
 * One visual row of three cards (the ETA card sits in the same row as Completing/min,
 * a locked honesty requirement): Completing/min, Enqueueing/min, and ETA-to-empty vs
 * next scheduled enqueue tick.
 *
 * Renders nothing when `throughput` or `ticks` is absent (old backend during deploy skew).
 * NEVER renders a numeric ETA unless eta_state is 'draining' - for every other state the
 * state word is shown instead (locked invariant mirrored from the backend; the FE trusts
 * eta_empty_* nullness and does not recompute).
 */

const numericSeries = (points, key) =>
	(Array.isArray(points) ? points : [])
		.map((point) => (point ? point[key] : null))
		.filter((value) => typeof value === 'number' && Number.isFinite(value));

const Chip = ({ variant, children }) => (
	<span className={`eta-chip badge ${variant}`}>{children}</span>
);

Chip.propTypes = {
	variant: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

const EtaCardContent = ({ throughput, ticks }) => {
	const nextTickAt = ticks.next && ticks.next.length > 0 ? ticks.next[0].at : null;

	switch (throughput.eta_state) {
		case 'draining': {
			if (throughput.eta_empty_at === null || throughput.eta_empty_at === undefined) {
				// Defensive: the backend invariant says eta_empty_at is non-null when
				// draining; if it ever is not, show the state word, never a number.
				return <Chip variant="bg-success">DRAINING</Chip>;
			}
			const overrun = Boolean(ticks.will_overrun_next_tick);
			return (
				<div>
					<div className={`eta-line fw-bold ${overrun ? 'text-danger' : 'text-success'}`}>
						{`ETA ${formatUtcHHmm(throughput.eta_empty_at)}Z | next tick ${formatUtcHHmm(nextTickAt)}Z (+${formatNumber(ticks.minutes_to_next_tick)} min)`}
					</div>
					{overrun && (
						<Chip variant="bg-danger">
							{`BACKLOG WILL COMPOUND - will overrun next tick ${formatEtTime(nextTickAt)} ET`}
						</Chip>
					)}
				</div>
			);
		}
		case 'not_draining':
			return <Chip variant="bg-warning text-dark">NOT DRAINING</Chip>;
		case 'rising':
			return <Chip variant="bg-warning text-dark">BACKLOG RISING</Chip>;
		case 'post_tick_ramp': {
			const recentAts = (ticks.recent || [])
				.map((tick) => minutesSince(tick.at))
				.filter((minutes) => minutes !== null);
			const minutesAgo = recentAts.length > 0 ? Math.min(...recentAts) : null;
			return (
				<div>
					<Chip variant="bg-warning text-dark">POST-TICK RAMP</Chip>
					{minutesAgo !== null && (
						<div className="eta-caption text-muted small">
							{`enqueue burst ${minutesAgo} min ago`}
						</div>
					)}
				</div>
			);
		}
		case 'idle':
			return <Chip variant="bg-light text-secondary border">QUEUE EMPTY</Chip>;
		case 'warming_up':
			return (
				<div>
					<Chip variant="bg-light text-secondary border">WARMING UP</Chip>
					<div className="eta-caption text-muted small">
						throughput history warming up
					</div>
				</div>
			);
		default:
			// Unknown future state: show the state word, never a number.
			return (
				<Chip variant="bg-light text-secondary border">
					{throughput.eta_state ? String(throughput.eta_state).toUpperCase() : EM_DASH}
				</Chip>
			);
	}
};

EtaCardContent.propTypes = {
	throughput: PropTypes.object.isRequired,
	ticks: PropTypes.object.isRequired,
};

const ThroughputStrip = ({ throughput, ticks, seriesPoints }) => {
	if (!throughput || !ticks) {
		// Old backend (deploy skew): the whole strip renders nothing.
		return null;
	}

	return (
		<div className="throughput-strip row g-3 mb-4">
			<div className="col-md-4">
				<StatCard
					title="Completing/min"
					value={formatRate(throughput.completed_per_min_30m)}
					icon="bi bi-speedometer2"
					spark={numericSeries(seriesPoints, 'completed_per_min')}
					tooltip="Successful job completions per minute over the trailing 30 minutes, derived from churn-immune id deltas. Higher is better."
				/>
			</div>
			<div className="col-md-4">
				<StatCard
					title="Enqueueing/min"
					value={formatRate(throughput.enqueued_per_min_30m)}
					icon="bi bi-plus-circle"
					spark={numericSeries(seriesPoints, 'enqueued_per_min')}
					tooltip="New jobs enqueued per minute over the trailing 30 minutes (MAX(jobs.id) delta). Spikes right after a scheduled tick are normal."
				/>
			</div>
			<div className="col-md-4">
				<div className="stat-card eta-card">
					<div className="stat-card-title">
						ETA to empty vs next tick
						<i
							className="bi bi-info-circle ms-2 text-muted stat-card-tooltip"
							title="Time to drain all pending jobs at the current trailing-30-min rate, compared with the next scheduled enqueue burst. Red means the backlog will grow tick-over-tick. No numeric ETA is shown unless the queue is actually draining."
							role="img"
							aria-label="About ETA to empty vs next tick"
						/>
					</div>
					<div className="stat-card-body">
						<div className="eta-card-content">
							<EtaCardContent throughput={throughput} ticks={ticks} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

ThroughputStrip.propTypes = {
	throughput: PropTypes.object,
	ticks: PropTypes.object,
	seriesPoints: PropTypes.array,
};

ThroughputStrip.defaultProps = {
	throughput: null,
	ticks: null,
	seriesPoints: undefined,
};

export default ThroughputStrip;
